import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import { renderIcon } from '@dopomogai/utils';

interface PlatformPillarProps {
  iconName: string;
  titleKey: string;
  textKey: string;
  visualMockup: React.ReactNode;
  reverseLayout?: boolean;
}

export const PlatformPillar: React.FC<PlatformPillarProps> = ({
  iconName,
  titleKey,
  textKey,
  visualMockup,
  reverseLayout = false,
}) => {
  const { t } = useTranslation('platform');

  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' },
    },
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-center"
    >
      {/* --- Text Content Column --- */}
      <div className={clsx('space-y-4', reverseLayout && 'md:order-last')}>
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 p-3 bg-slate-100 border-2 border-black rounded-lg">
            {renderIcon(iconName, { size: 28, className: "text-slate-800" })}
          </div>
          <h3 className="text-2xl font-bold text-gray-900 leading-tight">
            {t(titleKey)}
          </h3>
        </div>
        <p className="text-lg text-slate-600 leading-relaxed">
          {t(textKey)}
        </p>
      </div>

      {/* --- Visual Mockup Column --- */}
      <div className="w-full h-64 md:h-80 bg-slate-100 rounded-xl border-2 border-black p-4 flex items-center justify-center relative overflow-hidden">
        <div 
          className="absolute -inset-2 bg-grid-slate-300/40" 
          style={{ backgroundSize: '20px 20px' }} 
        />
        <div className="relative z-10 w-full h-full">
           {visualMockup}
        </div>
      </div>
    </motion.div>
  );
};

